/**
 * Écran 0 — Vérif technique (cf. maquette « Côté candidat » step 1,
 * CONTRACT §3/§4). Deux vérifs, SOUPLES (jamais bloquantes) :
 *   1. Appareil détecté côté client (`detectDeviceClass`) → carte 💻 ok /
 *      📱🖥 soft-warn (mobile/tablette passe quand même, juste marqué).
 *      `POST /device` tiré une seule fois au montage.
 *   2. Sonde connexion EXPLICITE : `PROBE_SAMPLE_COUNT` allers-retours réels
 *      (`candidateApi.ping`) étalés sur ~25 s, `Jauge` de la librairie, puis `POST /probe(phase:"probe")` — le
 *      verdict (✅/🟡/🔴) est calculé SERVEUR (cf. CONTRACT §3), jamais
 *      recalculé ici.
 * Le CTA « Continuer » reste désactivé tant que la sonde n'a pas terminé un
 * premier passage ; au-delà, il est toujours actif (y compris verdict 🔴 —
 * "Rejet auto seulement si catastrophique", jamais implémenté ici : aucun
 * seuil ne bloque la suite, cf. mission).
 */
import { useEffect, useRef, useState } from "react";

import { Alerte, Button, Etiquette, Jauge, StatTile } from "@/shared/ui";
import { Monitor, RefreshCw, Smartphone, Tablet } from "@/shared/ui/icons";

import { candidateApi } from "../api";
import { CONN_VERDICT_LABELS } from "../constants";
import type { ConnVerdict, DeviceClass } from "../types";
import {
  computeProbeStats,
  detectDeviceClass,
  PROBE_SAMPLE_COUNT,
  PROBE_SAMPLE_INTERVAL_MS,
  sampleOnce,
} from "./techCheck";

interface TechCheckScreenProps {
  token: string;
  onDone: () => void;
}

interface ProbeResultView {
  verdict: ConnVerdict;
  medianMs: number;
  stabilityPct: number;
}

// Le verdict se lit au TON de son `Alerte` (vert / ambre / rouge) et à son icône : la surface et le
// texte restent ceux de la librairie.
const VERDICT_TON: Record<ConnVerdict, "ok" | "attention" | "erreur"> = {
  good: "ok",
  ok: "attention",
  bad: "erreur",
};

const VERDICT_SUB: Record<ConnVerdict, string> = {
  good: "Tu es prêt à passer le test.",
  ok: "Ça ira. Reste dans un endroit stable.",
  bad: "Rapproche-toi du wifi et relance.",
};

function formatMs(ms: number): string {
  if (ms >= 1000) {
    return `${(ms / 1000).toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;
  }
  return `${Math.round(ms)} ms`;
}

export function TechCheckScreen({ token, onDone }: TechCheckScreenProps) {
  const [deviceClass] = useState<DeviceClass>(() => detectDeviceClass());
  const [probing, setProbing] = useState(true);
  const [progressPct, setProgressPct] = useState(0);
  const [result, setResult] = useState<ProbeResultView | null>(null);
  const runId = useRef(0);

  // Détection + confirmation "j'ai un ordinateur" (déduite, pas de UI de
  // dénonciation manuelle — cf. mission) : un seul POST, l'appareil ne
  // change pas en cours de route.
  useEffect(() => {
    candidateApi
      .postDevice(token, {
        device_class: deviceClass,
        computer_confirmed: deviceClass === "computer",
      })
      .catch(() => {
        // Best-effort : un POST /device raté ne doit jamais bloquer le
        // candidat, l'admin verra juste "non renseigné".
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function runProbe() {
    const myRun = ++runId.current;
    setProbing(true);
    setProgressPct(0);
    setResult(null);
    const samples: Array<number | null> = [];
    for (let i = 0; i < PROBE_SAMPLE_COUNT; i++) {
      if (runId.current !== myRun) return; // relancé entre-temps (↻ Relancer)
      const sample = await sampleOnce(token);
      samples.push(sample);
      if (runId.current !== myRun) return;
      setProgressPct(Math.round(((i + 1) / PROBE_SAMPLE_COUNT) * 100));
      if (i + 1 < PROBE_SAMPLE_COUNT) {
        await new Promise((r) => setTimeout(r, PROBE_SAMPLE_INTERVAL_MS));
      }
    }
    if (runId.current !== myRun) return;
    const stats = computeProbeStats(samples) ?? { rtt_median_ms: 9999, rtt_p95_ms: 9999, dropouts_pct: 100 };
    try {
      const res = await candidateApi.postProbe(token, { phase: "probe", ...stats });
      if (runId.current !== myRun) return;
      setResult({
        verdict: res.verdict,
        // 🪤 Le serveur ne fait que RÉ-ÉMETTRE les mesures qu'on vient de lui
        // envoyer, et son DTO les déclare toutes nullables. On retombe sur
        // `stats` — c'est la même donnée, mesurée ici. Seul `verdict` est à lui.
        medianMs: res.rtt_median_ms ?? stats.rtt_median_ms,
        stabilityPct: Math.max(0, Math.round(100 - (res.dropouts_pct ?? stats.dropouts_pct))),
      });
    } catch {
      if (runId.current !== myRun) return;
      // Le serveur n'a pas répondu — on affiche quand même un verdict local
      // pire-cas plutôt que de coincer le candidat sans jauge.
      setResult({
        verdict: "bad",
        medianMs: stats.rtt_median_ms,
        stabilityPct: Math.max(0, Math.round(100 - stats.dropouts_pct)),
      });
    }
    setProbing(false);
  }

  useEffect(() => {
    runProbe();
    // Un seul lancement automatique au montage — les relances suivantes
    // viennent du bouton « ↻ Relancer », pas de cet effet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finished = !probing && result !== null;
  const deviceOk = deviceClass === "computer";

  return (
    <div className="flex flex-1 flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Etiquette as="div">On prépare ton test</Etiquette>
        <h1 className="text-[26px] font-extrabold leading-tight tracking-tight text-fg text-balance">
          Deux vérifs rapides, et c'est parti
        </h1>
        <p className="text-[15px] leading-relaxed text-muted">
          On regarde ton matériel et ta connexion — pour être sûr que tu pourras bien travailler.
        </p>
      </div>

      {/* L'appareil : un statut (vert si c'est un ordinateur, ambre sinon — jamais bloquant), l'icône de l'appareil
          à la place de celle du ton. */}
      <Alerte
        ton={deviceOk ? "ok" : "attention"}
        className="text-[14.5px]"
        icone={deviceOk ? <Monitor /> : deviceClass === "tablet" ? <Tablet /> : <Smartphone />}
        titre={deviceOk ? "Ordinateur détecté" : "Tu es sur téléphone"}
        texte={
          deviceOk
            ? "Souris détectée. Parfait pour ce poste."
            : "Ce poste se fait sur ordinateur. Tu peux continuer — on te recontactera pour ça."
        }
      />

      <div className="flex flex-col gap-4">
        <Jauge
          etiquette={finished ? "Test terminé" : "Test de ta connexion…"}
          valeur={progressPct}
          affichage={<span className="tabular-nums">{progressPct} %</span>}
          taille="md"
        />

        {finished && result && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <StatTile tone="neutre" taille="sm" label="Latence médiane" value={formatMs(result.medianMs)} />
              <StatTile tone="neutre" taille="sm" label="Stabilité" value={`${result.stabilityPct} %`} />
            </div>

            <Alerte
              ton={VERDICT_TON[result.verdict]}
              className="text-[14.5px]"
              titre={`Connexion ${CONN_VERDICT_LABELS[result.verdict].toLowerCase()}`}
              texte={VERDICT_SUB[result.verdict]}
            />

            {result.verdict === "bad" && (
              <Button
                variant="secondary"
                size="lg"
                onClick={runProbe}
                className="min-h-12 w-full justify-center gap-2 text-[15px]"
              >
                <RefreshCw aria-hidden className="h-4 w-4" />
                Relancer le test de connexion
              </Button>
            )}
          </>
        )}
      </div>

      <Button
        size="xl"
        variant="accent"
        disabled={!finished}
        onClick={onDone}
        className="mt-auto w-full justify-center"
      >
        Continuer →
      </Button>
      <p className="text-center text-[13px] text-muted">
        {!finished
          ? "Vérification en cours…"
          : result?.verdict === "bad"
            ? "Connexion faible — tu peux relancer, ou continuer quand même."
            : "Environ 8 minutes · aucune expérience requise"}
      </p>
    </div>
  );
}
